import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Palette,
  Monitor,
  Smartphone,
  Play,
  Undo2,
  Redo2,
  Settings,
  MoreVertical,
  FileCode,
  Download,
  Copy,
} from 'lucide-react';

interface SlideToolbarProps {
  onToggleTheme: () => void;
  onToggleViewport: () => void;
  onPlay: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSettings: () => void;
  onImportMd: () => void;
  onExportMd: () => void;
  onCopyMd: () => void;
  viewportMode: 'desktop' | 'mobile';
  isPlayMode: boolean;
}

export default function SlideToolbar({
  onToggleTheme,
  onToggleViewport,
  onPlay,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSettings,
  onImportMd,
  onExportMd,
  onCopyMd,
  viewportMode,
  isPlayMode,
}: SlideToolbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Position dropdown above the trigger button (via portal)
  const toggleMenu = () => {
    if (!menuOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ x: rect.right, y: rect.top });
    }
    setMenuOpen(prev => !prev);
  };

  const moreItems = [
    { label: 'Importar MD', icon: FileCode, onClick: onImportMd },
    { label: 'Exportar MD', icon: Download, onClick: onExportMd },
    { label: 'Copiar MD', icon: Copy, onClick: onCopyMd },
  ];

  const btnBase = 'p-2 rounded-lg transition-colors';
  const btnDefault = `${btnBase} text-neutral-400 hover:text-white hover:bg-white/10`;
  const btnActive = `${btnBase} text-white bg-white/10`;
  const btnDisabled = `${btnBase} text-neutral-400 opacity-40 cursor-not-allowed`;

  return (
    <div className="flex items-center justify-center gap-1 px-4 py-2 border-t border-white/5 bg-neutral-900/60 backdrop-blur-xl">
      {/* Theme */}
      <button onClick={onToggleTheme} className={btnDefault} title="Tema">
        <Palette className="w-4 h-4" />
      </button>

      {/* Viewport toggle */}
      <div className="flex items-center bg-white/5 rounded-lg p-0.5">
        <button
          onClick={viewportMode === 'desktop' ? undefined : onToggleViewport}
          className={`p-1.5 rounded-md transition-colors ${viewportMode === 'desktop' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white'}`}
          title="Desktop"
        >
          <Monitor className="w-4 h-4" />
        </button>
        <button
          onClick={viewportMode === 'mobile' ? undefined : onToggleViewport}
          className={`p-1.5 rounded-md transition-colors ${viewportMode === 'mobile' ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white'}`}
          title="Preview simula iPhone 14 — runtime adapta ao device do respondente. Logo bottom aparece dentro do rodapé em telas com pergunta."
        >
          <Smartphone className="w-4 h-4" />
        </button>
      </div>

      {/* Play */}
      <button onClick={onPlay} className={isPlayMode ? btnActive : btnDefault} title={isPlayMode ? 'Parar preview' : 'Preview'}>
        <Play className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Undo */}
      <button
        onClick={canUndo ? onUndo : undefined}
        className={canUndo ? btnDefault : btnDisabled}
        disabled={!canUndo}
        title="Desfazer (Ctrl+Z)"
      >
        <Undo2 className="w-4 h-4" />
      </button>

      {/* Redo */}
      <button
        onClick={canRedo ? onRedo : undefined}
        className={canRedo ? btnDefault : btnDisabled}
        disabled={!canRedo}
        title="Refazer (Ctrl+Shift+Z)"
      >
        <Redo2 className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Settings */}
      <button onClick={onSettings} className={btnDefault} title="Configurações">
        <Settings className="w-4 h-4" />
      </button>

      {/* More menu trigger */}
      <button
        ref={triggerRef}
        onClick={toggleMenu}
        className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
        title="Mais opções"
      >
        <span className="hidden sm:inline text-xs">Mais</span>
        <MoreVertical className="w-4 h-4" />
      </button>

      {/* Portal dropdown — escapes all stacking contexts */}
      {menuOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 rounded-xl bg-neutral-900/95 backdrop-blur-xl border border-white/10 shadow-2xl py-1"
          style={{
            left: menuPos.x - 192, // w-48 = 192px, align right edge with trigger
            top: menuPos.y - 4 - (moreItems.length * 44 + 8), // above trigger
            zIndex: 9999,
          }}
        >
          {moreItems.map(item => (
            <button
              key={item.label}
              onClick={() => { item.onClick(); setMenuOpen(false); }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
